import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { ViewSchemaEditorComponent } from '../view-schema-editor/view-schema-editor.component';
import {
  MappingConnection,
  MappingEditorComponent,
  EntityFieldInfo,
  ViewFieldInfo,
} from './mapping-editor.component';
import { CatalogService, EntitySchemaCatalogItem } from '../../services/catalog.service';
import { ConverterService, IndexAppConvertRequest } from '../../services/converter.service';
import { EntitySchemaService } from '../../services/entity-schema.service';
import {
  buildIndexAppViewSchema,
  parseIndexAppViewSchema,
  safeParseJsonObject,
} from '../../utils/view-schema.utils';

@Component({
  selector: 'app-new-mapping',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ViewSchemaEditorComponent, MappingEditorComponent],
  templateUrl: './new-mapping.component.html',
  styleUrl: './new-mapping.component.scss',
})
export class NewMappingComponent {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly catalogService = inject(CatalogService);
  private readonly converterService = inject(ConverterService);
  private readonly entitySchemaService = inject(EntitySchemaService);

  protected readonly loadingCatalog = signal(false);
  protected readonly catalogError = signal<string | null>(null);
  protected readonly catalogItems = signal<EntitySchemaCatalogItem[]>([]);

  protected readonly isExecuting = signal(false);
  protected readonly executeError = signal<string | null>(null);
  protected readonly resultJson = signal<string>('');

  protected readonly loadingEntitySchema = signal(false);
  protected readonly entitySchemaError = signal<string | null>(null);
  protected readonly entitySchemaJson = signal<string>('');
  protected readonly entityFieldKeySuggestions = signal<string[]>([]);
  protected readonly entityFields = signal<EntityFieldInfo[]>([]);

  protected readonly viewFields = signal<ViewFieldInfo[]>([]);
  protected readonly mappingConnections = signal<MappingConnection[]>([]);

  protected readonly viewMode = signal<'raw'>('raw');

  protected readonly form = this.fb.group({
    searchPath: ['//garage-petabo/test/data/mrinAden', Validators.required],
    entitySchemaId: ['', Validators.required],
    schemaPath: ['', Validators.required],
    viewSchemaJson: [''],
  });

  constructor() {
    this.loadCatalog();

    this.form
      .get('entitySchemaId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => {
        const picked = this.catalogItems().find((x) => x.id === id);
        if (!picked) return;
        const schemaPath = picked.schemaPath ?? `settings/test/entitySchema/${picked.id}`;
        this.form.patchValue({ schemaPath }, { emitEvent: false });
        this.loadEntitySchema(schemaPath);
      });

    this.form
      .get('schemaPath')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((schemaPath) => {
        if (typeof schemaPath !== 'string' || !schemaPath.trim()) return;
        this.loadEntitySchema(schemaPath.trim());
      });

    // ViewSchema 側の field 一覧を追従（Rete の右側ポートに使う）
    this.form
      .get('viewSchemaJson')
      ?.valueChanges.pipe(
        debounceTime(100),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((text) => {
        const fields = this.extractViewFields(typeof text === 'string' ? text : '');
        this.viewFields.set(fields);
      });
  }

  loadCatalog(): void {
    this.loadingCatalog.set(true);
    this.catalogError.set(null);

    this.catalogService.getEntitySchemaCatalog().subscribe({
      next: (items) => {
        this.catalogItems.set(items);
        if (!this.form.get('entitySchemaId')?.value && items.length > 0) {
          const first = items[0];
          this.form.patchValue({
            entitySchemaId: first.id,
            schemaPath: first.schemaPath ?? `settings/test/entitySchema/${first.id}`,
          });
          this.loadEntitySchema(first.schemaPath ?? `settings/test/entitySchema/${first.id}`);
        }
        this.loadingCatalog.set(false);
      },
      error: (err) => {
        console.error('catalog load error', err);
        this.catalogError.set('カタログの取得に失敗しました。接続先とCORS設定を確認してください。');
        this.loadingCatalog.set(false);
      },
    });
  }

  loadEntitySchema(schemaPath: string): void {
    this.loadingEntitySchema.set(true);
    this.entitySchemaError.set(null);

    this.entitySchemaService.getEntitySchemaText(schemaPath).subscribe({
      next: (text) => {
        const trimmed = typeof text === 'string' ? text.trim() : '';
        this.entitySchemaJson.set(trimmed);

        // fields からキー候補を抽出（失敗してもプレビュー自体は表示する）
        try {
          const obj = JSON.parse(trimmed) as any;
          const fields = Array.isArray(obj?.fields) ? obj.fields : [];
          const normalized: EntityFieldInfo[] = fields.flatMap((f: any) => {
            const fieldName = typeof f?.fieldName === 'string' ? f.fieldName.trim() : '';
            if (!fieldName) return [];
            return [
              {
                fieldName,
                displayName: typeof f?.displayName === 'string' ? f.displayName : undefined,
                fieldType: typeof f?.fieldType === 'string' ? f.fieldType : undefined,
                length: typeof f?.length === 'number' ? f.length : undefined,
              } satisfies EntityFieldInfo,
            ];
          });

          const keys = normalized.map((x) => x.fieldName);
          const uniq = Array.from(new Set<string>(keys));
          this.entityFieldKeySuggestions.set(uniq.sort((a: string, b: string) => a.localeCompare(b)));
          this.entityFields.set(normalized);
        } catch {
          this.entityFieldKeySuggestions.set([]);
          this.entityFields.set([]);
        }

        this.loadingEntitySchema.set(false);
      },
      error: (err) => {
        console.error('entitySchema load error', err);
        this.entitySchemaError.set('EntitySchema の取得に失敗しました。schemaPath / bucketCode / CORS を確認してください。');
        this.entitySchemaJson.set('');
        this.entityFieldKeySuggestions.set([]);
        this.entityFields.set([]);
        this.loadingEntitySchema.set(false);
      },
    });
  }

  handleConnectionsChange(connections: MappingConnection[]): void {
    this.mappingConnections.set(connections);
    this.applyMappingToViewSchema(connections);
  }

  private extractViewFields(viewSchemaJsonText: string): ViewFieldInfo[] {
    try {
      const parsed = safeParseJsonObject(viewSchemaJsonText);
      const slice = parseIndexAppViewSchema(parsed);
      return slice.fields.map((f) => ({ fieldName: f.fieldName, formula: f.formula }));
    } catch {
      return [];
    }
  }

  private applyMappingToViewSchema(connections: MappingConnection[]): void {
    const currentText = this.form.get('viewSchemaJson')?.value;
    const current = typeof currentText === 'string' ? currentText : '';

    let slice = parseIndexAppViewSchema(null);
    let advanced: Record<string, unknown> | null = null;
    try {
      const parsed = safeParseJsonObject(current);
      slice = parseIndexAppViewSchema(parsed);
      advanced = safeParseJsonObject(slice.advancedJson);
    } catch {
      // 不正JSONのときは触らない（まずエディタ側で直してもらう）
      return;
    }

    const mapByView = new Map<string, string>();
    connections.forEach((c) => mapByView.set(c.toViewFieldName, c.fromEntityFieldName));

    const nextFields = slice.fields.map((f) => {
      const from = mapByView.get(f.fieldName);
      if (!from) return f; // 接続が無いなら既存formula維持
      return { ...f, formula: from };
    });

    const nextSchema = buildIndexAppViewSchema(
      {
        fields: nextFields,
        where: slice.where,
        filters: slice.filters,
        groupBy: slice.groupBy,
        orderBy: slice.orderBy,
        skip: slice.skip,
        take: slice.take,
      },
      advanced,
    );

    const nextText = nextSchema ? JSON.stringify(nextSchema, null, 2) : '';
    this.form.patchValue({ viewSchemaJson: nextText }, { emitEvent: false });
    this.viewFields.set(this.extractViewFields(nextText));
  }

  execute(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isExecuting.set(true);
    this.executeError.set(null);
    this.resultJson.set('');

    let viewSchema: unknown = undefined;
    const viewSchemaJson = this.form.get('viewSchemaJson')?.value;
    if (typeof viewSchemaJson === 'string' && viewSchemaJson.trim()) {
      try {
        viewSchema = JSON.parse(viewSchemaJson);
      } catch {
        this.executeError.set('View Schema (JSON) の形式が正しくありません');
        this.isExecuting.set(false);
        return;
      }
    }

    const req: IndexAppConvertRequest = {
      target: { searchPath: this.form.get('searchPath')!.value! },
      entitySchema: { schemaPath: this.form.get('schemaPath')!.value! },
      viewSchema,
    };

    this.converterService.convert(req, { simulation: true }).subscribe({
      next: (res) => {
        this.resultJson.set(JSON.stringify(res, null, 2));
        if (res.resultCode !== 0) {
          this.executeError.set(res.resultMessage || 'converter の実行に失敗しました。');
        }
        this.isExecuting.set(false);
      },
      error: (err) => {
        console.error('converter error', err);
        this.executeError.set('converter の呼び出しに失敗しました。接続先とCORS設定を確認してください。');
        this.isExecuting.set(false);
      },
    });
  }
}

